import styles from "../../../styles/pages/Employees/EmployeeTable.module.css";
import React, { useEffect, useState, useCallback, useContext } from "react";
import { LoadingCircle } from "../../../components/UI/Custom/LoadingCircle";
import { getAdmins } from "../../../utils/api/Admin/getAdminsApi";
import { handleError } from "../../../utils/custom/errorHandler";
import { LangContext } from "../../../context/LangContext";
import { ThemeContext } from "../../../context/ThemeContext";
import { createAdminLoginAsTokenApi } from "../../../utils/api/Admin/createAdminLoginAsTokenApi";
import { startLoginAsSession } from "../../../utils/custom/loginAsSession";


const translations = {
    ru: {
        accessDenied: "Доступ на просмотр списка сотрудников отсутствует.",
        noPermission: "У вас нет разрешения на просмотр списка сотрудников.",
        addEmployee: "Добавить Сотрудника",
        remove: "Удалить",
        filter: "Фильтр",
        loading: "Загрузка...",
        error: "Ошибка: ",
        noData: "Сотрудники не найдены.",
        tableHeaders: {
            fullName: "ФИО",
            email: "Email",
            position: "Позиция",
            desk: "Desk",
            actions: "Действия",
        },
        previous: "Пред.",
        next: "След.",
        loginAs: "Войти как",
    },
    en: {
        accessDenied: "Access to view the list of employees is denied.",
        noPermission: "You do not have permission to view the list of employees.",
        addEmployee: "Add Employee",
        remove: "Remove",
        filter: "Filter",
        loading: "Loading...",
        error: "Error: ",
        noData: "No employees found.",
        tableHeaders: {
            fullName: "Full Name",
            email: "Email",
            position: "Position",
            desk: "Desk",
            actions: "Actions",
        },
        previous: "Prev.",
        next: "Next.",
        loginAs: "Login as",
    },

};

export const EmployeeTable = ({ handlenewUserModal, handleEditUserModal, handleneOpenModal, shouldUpdate, filters, permissions, canLoginAsAdmins, currentAdminId }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const t = translations[langSelected] || translations.ru;

    const [employees, setEmployees] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 10;
    const [error, setError] = useState(null);
    const [loginAsAdminId, setLoginAsAdminId] = useState(null);

    const fetchEmployees = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            if (!permissions['user.list_admin_accounts']) {
                setEmployees([]);
                setTotalPages(1);
                setIsLoading(false);
                setError(t.noPermission);
                return;
            }

            const { data, meta } = await getAdmins(currentPage, itemsPerPage, filters);

            if (Array.isArray(data)) {
                setEmployees(data.map((user) => ({
                    id: user.id,
                    name: `${user.first_name} ${user.last_name}`,
                    email: user.email,
                    position: user.role.name,
                    desk: user.status,
                })));
                setTotalPages(meta.total_pages || 1);
            } else {
                console.error("API response data is not an array:", data);
                setEmployees([]);
                setTotalPages(1);
            }
        } catch (error) {
            console.error("Error fetching employee data:", error);
            handleError(error.response?.data || error, setError);
            setEmployees([]);
            setTotalPages(1);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, itemsPerPage, filters, permissions, t.noPermission]);

    useEffect(() => {
        fetchEmployees();
    }, [fetchEmployees, shouldUpdate]);

    const handlePreviousPage = () => {
        setCurrentPage(prevPage => Math.max(prevPage - 1, 1));
    };

    const handleNextPage = () => {
        setCurrentPage(prevPage => Math.min(prevPage + 1, totalPages));
    };

    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleLoginAs = async (adminId) => {
        try {
            setLoginAsAdminId(adminId);
            const authPayload = await createAdminLoginAsTokenApi(adminId);
            startLoginAsSession(authPayload);
            window.location.reload();
        } catch (loginAsError) {
            console.error('Error creating login-as session:', loginAsError);
            handleError(loginAsError, setError);
        } finally {
            setLoginAsAdminId(null);
        }
    };

    const renderPaginationButtons = () => {
        const pages = [];
        const maxPageButtons = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
        let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

        if (endPage - startPage + 1 < maxPageButtons) {
            startPage = Math.max(1, endPage - maxPageButtons + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    className={`${styles.pageButton} ${i === currentPage ? styles.activePage : ''} ${isDark ? styles.darkPageButton : ''} ${i === currentPage && isDark ? styles.darkActivePage : ''}`}
                    onClick={() => handlePageClick(i)}
                    disabled={isLoading}
                >
                    {i}
                </button>
            );
        }
        return pages;
    };

    if (!permissions['user.list_admin_accounts']) {
        return (
            <div className={`${styles.tableCard} ${isDark ? styles.darkTableCard : ''}`} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', fontSize: '20px', color: isDark ? '#b0b3b8' : '#888' }}>
                {t.accessDenied}
            </div>
        );
    }

    return (
        <div className={`${styles.tableCard} ${isDark ? styles.darkTableCard : ''}`}>
            <div className={styles.actionsBar}>
                <div className={styles.leftActions}>
                    {permissions['user.create_admin_accounts'] && (
                        <button className={`${styles.addButton} ${isDark ? styles.darkAddButton : ''}`} onClick={handlenewUserModal}>
                            {t.addEmployee}
                        </button>
                    )}

                    <button className={`${styles.removeButton} ${isDark ? styles.darkRemoveButton : ''}`}>
                        {t.remove}
                    </button>
                </div>
                <div className={styles.rightActions}>
                    <button className={`${styles.filterButton} ${isDark ? styles.darkFilterButton : ''}`} onClick={handleneOpenModal}>
                        {t.filter}
                    </button>
                </div>
            </div>
            {isLoading ? (
                <div className={styles.loadingOverlay}>
                    <LoadingCircle size={40} thickness={3} isDark={isDark} />
                </div>
            ) : error ? (
                <div className={`${styles.errorText} ${isDark ? styles.darkErrorText : ''}`}>{t.error}{error}</div>
            ) : (
                <div className={styles.tableContainer}>
                    <table className={styles.employeeTable}>
                        <thead className={`${styles.tableHeader} ${isDark ? styles.darkTableHeader : ''}`}>
                            <tr>
                                <th></th>
                                <th>{t.tableHeaders.fullName}</th>
                                <th>{t.tableHeaders.email}</th>
                                <th>{t.tableHeaders.position}</th>
                                <th>{t.tableHeaders.desk}</th>
                                <th>{t.tableHeaders.actions}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {employees.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className={`${styles.noDataMessage} ${isDark ? styles.darkNoDataMessage : ''}`}>
                                        {t.noData}
                                    </td>
                                </tr>
                            ) : (
                                employees.map((emp) => (
                                    <tr key={emp.id} className={`${styles.tableRow} ${isDark ? styles.darkTableRow : ''}`}>
                                        <td className={`${styles.tableCell} ${isDark ? styles.darkTableCell : ''}`}>
                                            <input type="checkbox" className={`${styles.checkboxInput} ${isDark ? styles.darkCheckboxInput : ''}`} />
                                        </td>
                                        <td
                                            className={`${styles.nameCell} ${styles.tableCell} ${isDark ? styles.darkTableCell : ''} ${permissions['user.update_admin_accounts'] ? styles.link : ''}`}
                                            onClick={permissions['user.update_admin_accounts'] ? () => handleEditUserModal(emp.id) : undefined}
                                        >
                                            {emp.name}
                                        </td>
                                        <td className={`${styles.emailCell} ${styles.tableCell} ${isDark ? styles.darkTableCell : ''}`}>
                                            {emp.email}
                                        </td>
                                        <td className={`${styles.positionCell} ${styles.tableCell} ${isDark ? styles.darkTableCell : ''}`}>
                                            {emp.position}
                                        </td>
                                        <td className={`${styles.deskCell} ${styles.tableCell} ${isDark ? styles.darkTableCell : ''}`}>
                                            {emp.desk}
                                        </td>
                                        <td className={`${styles.tableCell} ${styles.actionCell} ${isDark ? styles.darkTableCell : ''}`}>
                                            {canLoginAsAdmins && emp.id !== currentAdminId && (
                                                <button
                                                    type="button"
                                                    className={`${styles.loginAsButton} ${isDark ? styles.darkLoginAsButton : ''}`}
                                                    onClick={() => handleLoginAs(emp.id)}
                                                    disabled={loginAsAdminId === emp.id}
                                                >
                                                    {loginAsAdminId === emp.id ? `${t.loading}` : t.loginAs}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {!isLoading && totalPages > 1 && (
                <div className={`${styles.pagination} ${isDark ? styles.darkPagination : ''}`}>
                    <button
                        className={`${styles.pageButton} ${isDark ? styles.darkPageButton : ''}`}
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1 || isLoading}
                    >
                        {t.previous}
                    </button>
                    {renderPaginationButtons()}
                    <button
                        className={`${styles.pageButton} ${isDark ? styles.darkPageButton : ''}`}
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages || isLoading}
                    >
                        {t.next}
                    </button>
                </div>
            )}
        </div>
    );
};
